import {ComponentType, memo, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import WebToAppBanner from '../../../common/component/WebToAppBanner';
import {Page} from '../../../common/constant/page';
import useTypedSelector from '../../../common/hook/useTypedSelector';
import {getRouterQueryObject, routeTo} from '../../../common/util/PageRouter';
import MaxWidth from '../../../commonSub/component/MaxWidth';
import {WEB_TO_APP_EVENT_PROPS} from '../../../commonSub/constant';
import {actions as giftActions} from '../../../gift/state/action';
import {actions as moreActions} from '../../../more/state/action';
import MainV4 from '../../main/container/MainV4';
import MyPageV4 from '../../myPage/container/MyPageV4';
import TimeTableV4 from '../../timeTable/container/TimeTableV4';
import {NEED_TAB_QUERY_PAGE, setHeaderIsHidden} from '../common';
import HomeHeaderV4, {PADDING_TOP_STYLE_V4} from '../component/HomeHeaderV4';
import useHomePageView from '../hook/useHomePageView';
import {actions} from '../state/action';
import {
  getHomeSubTabId,
  sendHomeEvent,
  sendHomeViewImp,
  TRACKING_HOME_SECTION,
} from '../tracking';
import {HomeTabPage, ISecondTabItem} from '../type';

interface IProps {
  currentTabPage: HomeTabPage;
}

export default function HomeV4({currentTabPage}: IProps) {
  const dispatch = useDispatch();
  const [
    secondTabs,
    giftRedDotStatus,
    hasNewMore,
    isPreserveQueryParams,
    showHomePopup,
  ] = useTypedSelector(state => [
    state.home.secondTabs,
    state.gift.giftRedDotStatus,
    state.more.hasNewMore,
    state.home.isPreserveQueryParams,
    state.commonSub.showHomePopup,
  ]);
  const secondTab = secondTabs[currentTabPage].selected;

  const handleClickLogo = () => {
    const tabPage = HomeTabPage.Main;
    const selected = secondTabs[tabPage].tabList[0].key;
    dispatch(actions.changeSecondTabSelected({tabPage, selected}));
    handleClickHome(tabPage);
  };

  const handleClickHome = (tabPage: HomeTabPage) => {
    routeTo(
      {page: Page.Home, home: tabPage},
      {isReplace: false, isShallow: false}
    );

    sendHomeEvent({
      action: 'click',
      actionName: 'main_tab',
      customProps: {
        event_layer: 'tab',
        tab_id: TRACKING_HOME_SECTION[tabPage],
      },
    });
    window.scrollTo(0, 0);
  };

  const handleClickSecondTab = (item: ISecondTabItem) => {
    sendHomeEvent({
      actionName: 'sub_tab',
      action: 'click',
      customProps: {
        tab_id: getHomeSubTabId(currentTabPage, item.key),
        event_layer: 'sub_tab',
      },
    });

    setHeaderIsHidden(false);
    window.scrollTo(0, 0);

    if (NEED_TAB_QUERY_PAGE[currentTabPage]) {
      routeTo(
        {page: Page.Home, home: currentTabPage, tab: item.key},
        {isReplace: false, isShallow: false}
      );
    } else if (currentTabPage === HomeTabPage.Main) {
      const query = getRouterQueryObject();

      Object.keys(query).length > 0 &&
        routeTo({page: Page.Home}, {isReplace: true});
    }

    dispatch(
      actions.changeSecondTabSelected({
        tabPage: currentTabPage,
        selected: item.key,
      })
    );
  };

  useHomePageView(currentTabPage, secondTab);

  /**
   * 페이지 이동 시 계속 호출하기 위해 `useEffect` 사용.
   */
  useEffect(() => {
    dispatch(giftActions.fetchGiftRedDotStatus());
    dispatch(moreActions.fetchMoreRedDotStatus());
  }, [dispatch]);

  useEffect(() => {
    NEED_TAB_QUERY_PAGE[currentTabPage] &&
      isPreserveQueryParams &&
      dispatch(actions.setValue('isPreserveQueryParams', false));
  }, [dispatch, currentTabPage, isPreserveQueryParams]);

  const TabPageComponent = PAGE_COMPONENT_MAP[currentTabPage];
  return (
    <>
      <HomeHeaderV4
        hasNewGift={Object.values(giftRedDotStatus).some(item => item)}
        hasNewMore={hasNewMore}
        currentTabPage={currentTabPage}
        secondTabs={secondTabs}
        onClickLogo={handleClickLogo}
        onClickHome={handleClickHome}
        onClickSecondTab={handleClickSecondTab}
      />
      <MaxWidth style={PADDING_TOP_STYLE_V4} className="px-11" hasMdWidth>
        <TabPageComponent />
      </MaxWidth>
      {!(currentTabPage === HomeTabPage.Main && showHomePopup) && (
        <WebToAppBanner
          onIntersection={() =>
            sendHomeViewImp({customProps: WEB_TO_APP_EVENT_PROPS})
          }
        />
      )}
    </>
  );
}

const PAGE_COMPONENT_MAP: {[page in HomeTabPage]: ComponentType} = {
  [HomeTabPage.Main]: memo(MainV4),
  [HomeTabPage.MyPage]: memo(MyPageV4),
  [HomeTabPage.TimeTable]: memo(TimeTableV4),
};
